import axios from 'axios';
import type { AxiosResponse } from 'axios';

axios.defaults.headers.post['Access-Control-Allow-Origin'] = '*';

export type HeadRequestOptions = {
	followRedirects?: boolean;
	maxHops?: number;
	timeoutMs?: number;
};

export type HeadRequestData = {
	status: number;
	reason: string;
	requestUrl: string;
	location: string | null;
	headers: Record<string, string>;
};

export type HeadRequestResponse =
	| { success: true; data: HeadRequestData }
	| { success: false; error: string; data: null };

const requestHelper = {
	get: (url: string): Promise<AxiosResponse> => {
		return axios.get('/api' + url);
	},
	post: (url: string, data?: unknown): Promise<AxiosResponse> => {
		return axios.post('/api' + url, data);
	},
	getExternal: (url: string, headers?: Record<string, string>): Promise<AxiosResponse> => {
		if (headers) {
			return axios.get(url, { headers });
		}
		return axios.get(url);
	},
	postExternal: (url: string, data?: unknown, headers?: Record<string, string>): Promise<AxiosResponse> => {
		if (headers) {
			return axios.post(url, data, { headers });
		}
		return axios.post(url, data);
	},

	/**
	 * Ask the extension background to perform a HEAD (or fallback GET with Range)
	 * and return headers + status (bypassing browser CORS).
	 *
	 * Requires a background listener for { type: 'head_request', url, followRedirects, maxHops }
	 * that responds with { success: boolean, data?: HeadRequestData, error?: string }.
	 */
	headRequest: (url: string, opts: HeadRequestOptions = {}): Promise<HeadRequestResponse['data']> => {
		const followRedirects = false;
		const maxHops = 5;
		const timeoutMs = 15000;
		const messageType = 'head_request';

		return new Promise((resolve, reject) => {
			if (!url) return reject(new Error('Missing url'));

			const id = Math.random().toString(36).slice(2);

			const onMessage = (e: MessageEvent) => {
				const msg = e.data;
                //console.log('msg', msg)
                
                //if the message is an api_response from ZigiParser and the id matches, remove the listener
                if(msg.type =='api_response' && msg.id == id){
                    window.removeEventListener('message', onMessage);

                    //IF WE HAVE DATA
                    if(msg?.response?.data){
                        resolve(msg.response.data);
                    }
                    else{
                        reject(new Error('No data'));
                    }
                }
                
                return;
			};

			window.addEventListener('message', onMessage);
			const timer = setTimeout(() => {
				window.removeEventListener('message', onMessage);
				reject(new Error(`headRequest timed out after ${timeoutMs}ms`));
			}, timeoutMs);

			window.postMessage({ __ext__: 'HEAD_REQUEST', id, url, followRedirects, maxHops, type: messageType }, window.location.origin);
		});
	},
};

export default requestHelper;
